import './nipple.scss';

import { IcCrosshair } from '@assets/icons/cameras';
import { AbsoluteCenter, Box, Center, HStack, Text } from '@chakra-ui/react';
import Player from '@components/common/video/Player';
import { ReplayPlayer } from '@components/common/video/ReplayPlayer';
import CameraTrackingObjects from '@components/video/cameraControl/CameraTrackingObjects';
import { useAppSelector } from '@hooks/redux.hooks';
import { UseCameraCommandFunctions } from '@hooks/useCameraCommand';
import { useDimensions } from '@hooks/useDimensions';
import { useWithDispatch } from '@hooks/useWithDispatch';
import {
  addSelectedCameraStreams,
  removeSelectedCameraStreams,
  updateJoystickControlId,
  updateJoystickControlledCam,
} from '@redux/global/global.reducer';
import { selectJoystickControlId } from '@redux/global/global.selector';
import { selectTrackLastPositionFromRawSituationByUniqueKey } from '@redux/situation/situation.selectors';
import { modulo } from '@utils/math.utils';
import { HolopticsSubCameraEnum } from '@utils/sensors/camera/camera.constants';
import { calculateVerticalFov, computePlayerStreamUrl, getDefaultFlux } from '@utils/sensors/camera/camera.utils';
import { isSensorOrSubSensorConfigurationOfType } from '@utils/sensors/sensors.utils';
import { isEqual } from 'lodash';
import React, { Dispatch, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ReactNipple from 'react-nipple';

import { VideoBox } from '@/types/c2/c2.types';
import { CameraType } from '@/types/sensor/configuration.types';
import { MonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { TrackerLocation } from '@/types/sensor/sensor.types';
import {
  HolopticsSensorConfiguration,
  SensorTypeEnum,
  SubCameraConfiguration,
  TrackingStatusEnum,
  TrackingTypeEnum,
} from '@/types/sensor/sensors';

import AutomaticTurnOffPopup from './AutomaticTurnOffPopup';
import CameraBorder from './CameraBorder';
import CameraControlTitle from './CameraControlTitle';
import { CameraStatusInfo } from './CameraStatusInfo';
import AzimuthCompass from './gauge/azimuth/AzimuthCompass';
import ElevationCompass from './gauge/elevation/ElevationCompass';
import HorizontalGauge from './gauge/HorizontalGauge';
import VerticalGauge from './gauge/VerticalGauge';
import PipContainer from './PipContainer';

export enum GaugeStyleEnum {
  GAUGE = 'GAUGE',
  COMPASS = 'COMPASS',
  BOTH = 'BOTH',
  NONE = 'NONE',
}

export type ControlState = {
  pip: boolean;
  displayedFlux: SubCameraConfiguration | null;
  pipFlux: SubCameraConfiguration[];
  displayCross: boolean;
  previousSelectedCameraKey: string | null;
  polarity: boolean;
  iso: boolean;
  hybridFocus: boolean;
  whiteBalance: boolean;
  round: boolean;
  simpleRoundSpeed: number | null;
  displayVirtualJoystick: boolean;
  gaugeStyle: GaugeStyleEnum;
  fullscreen: boolean;
  mwirFilters: boolean;
};

interface OwnProps {
  replayMode: boolean;
  selectedCameraKey: string | null;
  replayLadFlux: SubCameraConfiguration | null;
  uniqueKey: string;
  monitoringSensor: MonitoringSensorByType<CameraType> | null;
  controlState: ControlState;
  setControlState: Dispatch<Partial<ControlState>>;
  useCamera: UseCameraCommandFunctions;
}

const JOYSTICK_CONTAINER_SIZE = 180;
const DEFAULT_CAMERA_RATIO = 16 / 9;

type JoystickData = {
  angle: {
    degree: number;
  };
  distance: number;
};

function CameraControl({
  replayMode,
  selectedCameraKey,
  uniqueKey,
  monitoringSensor,
  controlState,
  setControlState,
  replayLadFlux,
  useCamera,
}: Readonly<OwnProps>) {
  const updateJoystickControlledCamWithDispatch = useWithDispatch(updateJoystickControlledCam);
  const updateJoystickControlIdWithDispatch = useWithDispatch(updateJoystickControlId);
  const updateSelectedCameraStreams = useWithDispatch(addSelectedCameraStreams);
  const clearSelectedCameraStreams = useWithDispatch(removeSelectedCameraStreams);
  const [componentDimension, componentRef] = useDimensions();
  const [dimension16_9, ref16_9] = useDimensions();
  const [effectiveVideoDimension, effectiveVideoRef] = useDimensions();

  const [zoomTimeout, setZoomTimeout] = useState<NodeJS.Timeout | null>(null);
  const wheelZoomDelay = 500;

  const trackingBoxDimensions = {
    width: (effectiveVideoDimension?.width ?? 0) / 10,
    height: (effectiveVideoDimension?.height ?? 0) / 10,
  };

  const joystickControlId = useAppSelector(selectJoystickControlId);

  const detectedObjects = useAppSelector(
    (state) => selectTrackLastPositionFromRawSituationByUniqueKey(state, replayMode, selectedCameraKey),
    isEqual,
  );

  useEffect(() => {
    let flux = controlState.displayedFlux;
    if (replayMode) {
      flux = replayLadFlux;
      setControlState({ displayedFlux: flux });
    }

    if (flux) {
      updateSelectedCameraStreams(flux);
      return () => {
        clearSelectedCameraStreams(flux);
      };
    }
  }, [
    clearSelectedCameraStreams,
    updateSelectedCameraStreams,
    replayLadFlux,
    controlState.displayedFlux,
    setControlState,
    replayMode,
  ]);

  const cameraStatus = monitoringSensor?.status ?? null;
  const cameraConfiguration = monitoringSensor?.configuration ?? null;
  const displayedVideoStatus = cameraStatus?.videoStatuses.find((vs) => vs.id === controlState.displayedFlux?.cameraId);

  const videoTrackingStateOfSelectedFlux =
    controlState.displayedFlux !== null && cameraStatus?.trackingStatus.videoTrackingStatus.holopticsVideoTrackings
      ? cameraStatus.trackingStatus.videoTrackingStatus.holopticsVideoTrackings[controlState.displayedFlux.cameraId]
          ?.status
      : TrackingStatusEnum.DISABLED;

  const isVideoTrackingOngoing =
    cameraStatus?.trackingStatus.globalTrackingType === TrackingTypeEnum.VIDEO &&
    cameraStatus.trackingStatus.videoTrackingStatus.globalVideoTrackingState === TrackingStatusEnum.ACTIVE;

  const isSensorTrackingOngoing =
    cameraStatus?.trackingStatus.globalTrackingType === TrackingTypeEnum.SENSOR &&
    cameraStatus.trackingStatus.sensorTrackingStatus.globalSensorTrackingState === TrackingStatusEnum.ACTIVE;

  const isBufferized =
    controlState.displayedFlux !== null && cameraStatus?.trackingStatus.videoTrackingStatus.holopticsVideoTrackings
      ? cameraStatus.trackingStatus.videoTrackingStatus.holopticsVideoTrackings[controlState.displayedFlux.cameraId]
          ?.isBufferized
      : false;

  const isHolopticsAf2206 = isSensorOrSubSensorConfigurationOfType(cameraConfiguration, SensorTypeEnum.AF2206);
  const isHolopticsAxis = isSensorOrSubSensorConfigurationOfType(cameraConfiguration, SensorTypeEnum.AXIS);
  const isHolopticsAf2206IrCamera =
    isHolopticsAf2206 &&
    (controlState.displayedFlux?.cameraId === HolopticsSubCameraEnum.MWIR ||
      controlState.displayedFlux?.cameraId === HolopticsSubCameraEnum.SWIR);

  if (controlState.previousSelectedCameraKey !== selectedCameraKey) {
    setControlState({
      displayedFlux: getDefaultFlux(cameraConfiguration),
      pipFlux:
        cameraConfiguration?.subCameras.filter((sc) => sc.code !== getDefaultFlux(cameraConfiguration)?.code) ?? [],
      previousSelectedCameraKey: selectedCameraKey,
    });
  }

  useEffect(() => {
    if (joystickControlId === uniqueKey) {
      updateJoystickControlledCamWithDispatch({
        cameraUniqueKey: selectedCameraKey,
        subCameraId: controlState.displayedFlux?.cameraId,
      });
    }
  }, [
    selectedCameraKey,
    joystickControlId,
    uniqueKey,
    updateJoystickControlledCamWithDispatch,
    updateJoystickControlIdWithDispatch,
    controlState.displayedFlux?.cameraId,
  ]);

  const holopticsIrLetterboxSize = {
    width: isHolopticsAf2206IrCamera ? ((dimension16_9?.width ?? 0) - (effectiveVideoDimension?.width ?? 0)) / 2 : 0,
    height: isHolopticsAf2206IrCamera ? ((dimension16_9?.height ?? 0) - (effectiveVideoDimension?.height ?? 0)) / 2 : 0,
  };

  const trackAt = useCallback(
    (clickPosition: { x: number; y: number }, resX: number, resY: number) => {
      if (
        !selectedCameraKey ||
        controlState.displayedFlux === null ||
        dimension16_9 === null ||
        effectiveVideoDimension === null
      ) {
        return;
      }
      if (isHolopticsAf2206 || isHolopticsAxis) {
        const videoBox: VideoBox = {
          x: +((clickPosition.x + holopticsIrLetterboxSize.width) / effectiveVideoDimension.width).toFixed(5) * resX,
          y: +((clickPosition.y + holopticsIrLetterboxSize.height) / effectiveVideoDimension.height).toFixed(5) * resY,
          width: trackingBoxDimensions.width,
          height: trackingBoxDimensions.height,
        };
        useCamera.videoTracking(videoBox, TrackingStatusEnum.ACTIVE, null);
      } else {
        console.warn('ZMER video tracking soon to be implemented');
      }
    },
    [
      selectedCameraKey,
      controlState.displayedFlux,
      dimension16_9,
      effectiveVideoDimension,
      isHolopticsAf2206,
      isHolopticsAxis,
      holopticsIrLetterboxSize.width,
      holopticsIrLetterboxSize.height,
      trackingBoxDimensions.width,
      trackingBoxDimensions.height,
      useCamera,
    ],
  );

  const pointAt = useCallback(
    (clickPosition: { x: number; y: number }) => {
      const displayedVideoStatus = cameraStatus?.videoStatuses.find(
        (vs) => vs.id === controlState.displayedFlux?.cameraId,
      );
      if (
        !effectiveVideoDimension ||
        controlState.displayedFlux === null ||
        displayedVideoStatus === undefined ||
        cameraStatus === null
      ) {
        return;
      }
      if (isHolopticsAxis) {
        useCamera.pointAtPixel(
          effectiveVideoDimension.width,
          effectiveVideoDimension.height,
          clickPosition.x,
          clickPosition.y,
        );
        return;
      }

      useCamera.pointAt(
        clickPosition.x,
        clickPosition.y,
        effectiveVideoDimension.width,
        effectiveVideoDimension.height,
      );
    },
    [cameraStatus, effectiveVideoDimension, controlState.displayedFlux, useCamera, isHolopticsAxis],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (controlState.displayedFlux === null || componentDimension === null || effectiveVideoDimension === null) {
        return;
      }
      //this is the position of the click from the top left of the effective video area.
      const effectiveClickPosition = {
        x: e.clientX - e.currentTarget.getBoundingClientRect().left,
        y: e.clientY - e.currentTarget.getBoundingClientRect().top,
      };

      const resX = controlState.displayedFlux.resolutionX;
      const resY = controlState.displayedFlux.resolutionY;

      //This is a temporary fix for x-lad (03/25), this will have to be refactored when we have the de-buffering function
      if (e.shiftKey) {
        if (isBufferized) {
          trackAt(effectiveClickPosition, resX, resY);
        } else {
          useCamera.videoTracking(null, TrackingStatusEnum.PENDING, null);
        }
      } else if (!isVideoTrackingOngoing) {
        if (isSensorTrackingOngoing) {
          useCamera.stopAllTrackings(null, false);
        }
        pointAt(effectiveClickPosition);
      }
    },
    [
      controlState.displayedFlux,
      componentDimension,
      effectiveVideoDimension,
      isBufferized,
      isVideoTrackingOngoing,
      trackAt,
      isSensorTrackingOngoing,
      pointAt,
      useCamera,
    ],
  );

  useEffect(() => {
    const handleAutoTracking = (event: KeyboardEvent) => {
      if (videoTrackingStateOfSelectedFlux === TrackingStatusEnum.SCANNING) {
        const numPadKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        if (numPadKeys.includes(event.key)) {
          const containsKey = detectedObjects.some((d: TrackerLocation) => d.id?.toString() === event.key);
          if (containsKey) {
            useCamera.videoTracking(null, TrackingStatusEnum.ACTIVE, event.key);
          }
        }
      }
    };
    document.addEventListener('keydown', handleAutoTracking);
    return () => {
      document.removeEventListener('keydown', handleAutoTracking);
    };
  }, [detectedObjects, useCamera, videoTrackingStateOfSelectedFlux]);

  const handleZoom = useCallback(
    (e: React.WheelEvent) => {
      if (zoomTimeout) {
        clearTimeout(zoomTimeout);
      }
      if (e.deltaY < 0) {
        useCamera.zoomIn();
      } else if (e.deltaY > 0) {
        useCamera.zoomOut();
      }
      const timeout = setTimeout(() => {
        useCamera.zoomStop();
      }, wheelZoomDelay);
      setZoomTimeout(timeout);
    },
    [useCamera, zoomTimeout],
  );

  useEffect(() => {
    return () => {
      if (zoomTimeout) {
        clearTimeout(zoomTimeout);
      }
    };
  }, [zoomTimeout]);

  function onMoveVirtualJoystick(data: JoystickData) {
    const angle = data.angle.degree;
    //The virtual joystick's magnitude is based on half of the nipple container size hence the division by JOYSTICK_CONTAINER_SIZE / 2
    const dx = (Math.cos((angle * Math.PI) / 180) * data.distance) / (JOYSTICK_CONTAINER_SIZE / 2);
    const dy = (Math.sin((angle * Math.PI) / 180) * data.distance) / (JOYSTICK_CONTAINER_SIZE / 2);
    useCamera.moveJoystick(dx, dy);
  }

  // The holoptics IR is a 1280x1024 flux embedded in a 1920x1080 flux. It will always stay this way.
  // Therefore, in order to get the correct useful video dimension, we need to multiply the 16:9 width by 1280/1920
  // and the 16:9 height by 1024/1080. These ratios explain the thin black strips on the top and bottom of the ir video.
  const effectiveVideoPlayerWidth = isHolopticsAf2206IrCamera
    ? ((dimension16_9?.width ?? 0) * 2) / 3
    : (dimension16_9?.width ?? 0);
  const effectiveVideoPlayerHeight = isHolopticsAf2206IrCamera
    ? (dimension16_9?.height ?? 0) / 1.0546875
    : (dimension16_9?.height ?? 0);

  const playerId = `control-player-${uniqueKey}`;
  const displayPlayer = useCallback(() => {
    if (selectedCameraKey && cameraStatus && controlState.displayedFlux) {
      return (
        <Center width="100%" height="100%" position="relative" ref={componentRef}>
          <Box
            width={`min(100%, calc(${componentDimension?.height ?? 0}px * ${DEFAULT_CAMERA_RATIO}))`}
            height={`min(100%, calc(${componentDimension?.width ?? 0}px * ${1 / DEFAULT_CAMERA_RATIO}))`}
            position="relative"
            ref={ref16_9}
          >
            {replayMode ? (
              <ReplayPlayer
                src={controlState.displayedFlux.replayUrl}
                id={playerId}
                generatedByNimble={controlState.displayedFlux.generateNimbleUrl}
              />
            ) : (
              <Player
                id={playerId}
                displayMessageCentered={!controlState.displayCross}
                src={computePlayerStreamUrl(controlState.displayedFlux)}
                playerType={controlState.displayedFlux.playerType}
              />
            )}

            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              width={`${effectiveVideoPlayerWidth}px`}
              height={`${effectiveVideoPlayerHeight}px`}
              ref={effectiveVideoRef}
              onClick={handleClick}
              onWheel={handleZoom}
            />
          </Box>
        </Center>
      );
    }

    const statusMessage = !cameraStatus ? 'cameras.noCameraStatus' : 'cameras.noFlux';

    return (
      <AbsoluteCenter>
        <Text transform={controlState.displayCross ? 'translateY(100px)' : undefined}>
          <FormattedMessage id={!selectedCameraKey ? 'cameras.noSelectedCamera' : statusMessage} />
        </Text>
      </AbsoluteCenter>
    );
  }, [
    selectedCameraKey,
    cameraStatus,
    controlState.displayedFlux,
    controlState.displayCross,
    componentRef,
    componentDimension?.height,
    componentDimension?.width,
    ref16_9,
    playerId,
    replayMode,
    effectiveVideoPlayerWidth,
    effectiveVideoPlayerHeight,
    effectiveVideoRef,
    handleClick,
    handleZoom,
  ]);

  const pan360: number = cameraStatus?.pan ? Number(modulo(cameraStatus.pan, 360).toFixed(2)) : 0;

  return (
    <>
      <CameraBorder
        hasControl={joystickControlId === uniqueKey}
        isSensorTracking={isSensorTrackingOngoing}
        isVideoTracking={videoTrackingStateOfSelectedFlux === TrackingStatusEnum.ACTIVE}
      />
      <CameraControlTitle replayMode={replayMode} />
      {displayPlayer()}
      {cameraStatus && (
        <Box top={replayMode ? '85px' : '50px'} position="absolute" pointerEvents="none">
          <CameraStatusInfo
            maintenance={cameraConfiguration?.maintenance}
            cameraStatus={cameraStatus}
            cameraId={controlState.displayedFlux?.cameraId}
          />
        </Box>
      )}
      {cameraStatus && (
        <>
          {(controlState.gaugeStyle === GaugeStyleEnum.BOTH || controlState.gaugeStyle === GaugeStyleEnum.GAUGE) && (
            <>
              <HorizontalGauge
                pan={pan360}
                fov={
                  displayedVideoStatus?.fov && displayedVideoStatus.fov > 0
                    ? Number(displayedVideoStatus.fov.toFixed(1))
                    : 0
                }
                cameraPosition={cameraConfiguration!.sensorPosition}
                replayMode={replayMode}
              />
              <VerticalGauge
                tilt={cameraStatus.tilt !== null ? Number(cameraStatus.tilt.toFixed(2)) : 0}
                fov={calculateVerticalFov(
                  displayedVideoStatus?.fov,
                  controlState.displayedFlux?.resolutionX,
                  controlState.displayedFlux?.resolutionY,
                )}
                min={controlState.displayedFlux ? Math.max(controlState.displayedFlux.minViewAngle, -90) : -90}
                max={controlState.displayedFlux ? Math.min(controlState.displayedFlux.maxViewAngle, 90) : 90}
                cameraPosition={cameraConfiguration!.sensorPosition}
                cameraAltitude={cameraConfiguration!.sensorAltitude}
                replayMode={replayMode}
              />
            </>
          )}
          {isBufferized && videoTrackingStateOfSelectedFlux === TrackingStatusEnum.DISABLED && (
            <Center position="absolute" bottom="100px" width="100%">
              <Text>
                <FormattedMessage id="cameras.cameraMenu.buffered" />
              </Text>
            </Center>
          )}

          {videoTrackingStateOfSelectedFlux === TrackingStatusEnum.SCANNING && (
            <Center position="absolute" bottom="100px" width="100%">
              <Text whiteSpace="pre-line">
                <FormattedMessage id="cameras.cameraMenu.scanning" />
              </Text>
            </Center>
          )}
          {controlState.displayVirtualJoystick && (
            <ReactNipple
              options={{
                mode: 'static',
                position: {
                  bottom: `${110 + JOYSTICK_CONTAINER_SIZE / 2}px`,
                  right: `${56 + JOYSTICK_CONTAINER_SIZE / 2}px`,
                },
                size: JOYSTICK_CONTAINER_SIZE,
              }}
              className="custom-nipple"
              key={controlState.fullscreen}
              onMove={(_e: unknown, data: unknown) => onMoveVirtualJoystick(data as JoystickData)}
              onEnd={() => useCamera.move({ panSpeed: 0, tiltSpeed: 0 })}
            />
          )}
          {(controlState.gaugeStyle === GaugeStyleEnum.BOTH || controlState.gaugeStyle === GaugeStyleEnum.COMPASS) && (
            <HStack gap={9.5} position="absolute" left="24px" bottom="110px">
              <ElevationCompass
                tilt={cameraStatus.tilt !== null ? Number(cameraStatus.tilt.toFixed(2)) : 0}
                fov={calculateVerticalFov(
                  displayedVideoStatus?.fov,
                  controlState.displayedFlux?.resolutionX,
                  controlState.displayedFlux?.resolutionY,
                )}
                replayMode={replayMode}
                min={controlState.displayedFlux ? Math.max(controlState.displayedFlux.minViewAngle, -90) : -90}
                max={controlState.displayedFlux ? Math.min(controlState.displayedFlux.maxViewAngle, 90) : 90}
                cameraAltitude={cameraConfiguration!.sensorAltitude}
                cameraPosition={cameraConfiguration!.sensorPosition}
              />
              <AzimuthCompass
                pan={pan360}
                replayMode={replayMode}
                cameraPosition={cameraConfiguration!.sensorPosition}
                fov={
                  displayedVideoStatus?.fov && displayedVideoStatus.fov > 0
                    ? Number(displayedVideoStatus.fov.toFixed(1))
                    : 0
                }
              />
            </HStack>
          )}
        </>
      )}
      {controlState.pipFlux.length > 0 && (
        <PipContainer state={controlState} setState={setControlState} replayMode={replayMode} uniqueKey={uniqueKey} />
      )}
      {controlState.displayCross && (
        <AbsoluteCenter pointerEvents="none">
          <IcCrosshair />
        </AbsoluteCenter>
      )}
      {(videoTrackingStateOfSelectedFlux === TrackingStatusEnum.ACTIVE ||
        videoTrackingStateOfSelectedFlux === TrackingStatusEnum.SCANNING) && (
        <CameraTrackingObjects
          controlState={controlState}
          selectedCameraKey={selectedCameraKey}
          replayMode={replayMode}
          dimension16_9={dimension16_9}
          componentDimension={componentDimension}
          effectiveVideoDimension={effectiveVideoDimension}
          holopticsIrLetterboxSize={holopticsIrLetterboxSize}
          trackingAction={videoTrackingStateOfSelectedFlux}
          detectedObjects={detectedObjects}
        />
      )}
      {isHolopticsAf2206IrCamera && (
        <AutomaticTurnOffPopup
          cameraConfiguration={cameraConfiguration as HolopticsSensorConfiguration}
          replayMode={replayMode}
          displayedCameraId={controlState.displayedFlux!.cameraId}
        />
      )}
    </>
  );
}

export default CameraControl;
